import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getCssVar, isAdminLayout, layoutPath } from '../../utils';
import { IconLabel } from '../IconLabel';

export const LayoutSwitcher: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { pathname, search } = useLocation();

  const { storyId, chatId, organisationTagId } = useParams();

  const readerHome = useMemo(() => '/', []);

  const adminHome = useMemo(() => '/admin', []);

  const mirrorPage = useMemo(() => {
    if (pathname === readerHome) {
      return adminHome;
    }

    if (isAdminLayout()) {
      return `${pathname.replace(adminHome, '')}${search}`;
    }

    return `${adminHome}${pathname}${search}`;
  }, [adminHome, pathname, readerHome, search]);

  const navigatePath = useMemo(() => {
    switch (pathname) {
      //SHARED PAGES
      case layoutPath('/'):
      case layoutPath('/profile'):
      case layoutPath('/profile/items'):
      case layoutPath('/chats'):
      case layoutPath(`/chats?chatId=${chatId}`):
      case layoutPath('/search'):
      case layoutPath('/storyTab1'):
      case layoutPath(`/storyTab1/${storyId}/items`):
      case layoutPath('/storyTab2'):
      case layoutPath(`/storyTab2/${storyId}/items`):
      case layoutPath('/storyTab3'):
      case layoutPath(`/storyTab3/${storyId}/items`):
      case layoutPath('/storyTab4'):
      case layoutPath(`/storyTab4/${storyId}/items`):
      case layoutPath('/storyTab5'):
      case layoutPath(`/storyTab5/${storyId}/items`):
      case layoutPath(`/pinnedStoryTab/${storyId}/items`):
      case layoutPath(`/tags/${organisationTagId}`):
        return mirrorPage;
      //READER PAGES
      case '/userDirectory':
        return adminHome;
      //ADMIN PAGES
      case '/admin/storyTab':
      case '/admin/pinnedStoryTab':
      case '/admin/orgSettings':
      case '/admin/analytics':
      case '/admin/notifications':
      case '/admin/pushNotifications':
        return readerHome;
      default:
        return isAdminLayout() ? readerHome : adminHome;
    }
  }, [adminHome, chatId, mirrorPage, organisationTagId, pathname, readerHome, storyId]);

  const label = useMemo(
    () => t(`common.${isAdminLayout() ? 'reader-view' : 'admin-dashboard'}`),
    [t]
  );

  return (
    <IconLabel
      iconId={'switch'}
      label={label}
      color={getCssVar('--base-link-text-color')}
      hoverColor={getCssVar('--base-link-text-hover-color')}
      onClick={() => navigate(navigatePath)}
    />
  );
};
